<script setup lang="ts">
import { reactive, computed, ref, watch, toRef } from 'vue';
import { useLocalStorage } from '@zoomcatalog/composable';
import { useStackableNotificationsHandlers } from '@zoomcatalog/design-system';
import {
  useMakePageSetupInstance,
  mergeProductSettings,
  useMakeProductSidebarInstance,
  Page,
  isOldSettings,
  transformOldSettings,
  mergeTemplateSetting,
  useMakePageOrientationsInstance,
  overrideDataTypeGuards,
  Product,
  formatNewProductPricing,
} from '@zoomcatalog/design-templates';
import { AxiosResponse } from 'axios';
import { useRoute, useRouter } from 'vue-router';
import DesignEmpty from '@/components/DesignEmpty.vue';
import DesignLocked from '@/components/DesignLocked.vue';
import DesignPrivate from '@/components/DesignPrivate.vue';
import PageLoader from '@/components/PageLoader.vue';
import { useCookie } from '@/composable/useCookie';
import useFileDownload from '@/composable/useFileDownload';
import { useMakePaginationInstance } from '@/composable/usePagination';
import { useQueryWrapper } from '@/composable/useQueryWrapper';
import {
  setImplicitTokenInCookie,
  useMakeTrackingInstance,
} from '@/composable/useTracking';
import { ZDesign } from '@/features/design';
import {
  ImplicitTokenResponse,
  useAuthService,
} from '@/service/request-handlers/useAuthService';
import { useCompanyByIdQuery } from '@/service/request-handlers/useCompanyService';
import {
  isProductLibrarySetting,
  useCompanySettingSectionQuery,
} from '@/service/request-handlers/useCompanySettingSectionQuery';
import {
  GeolocationData,
  useGeolocationQuery,
} from '@/service/request-handlers/useGeolocationService';
import {
  useGetDesign,
  useGetPageByIdProject,
} from '@/service/request-handlers/useQuickCanvasService';
import {
  DEFAULT_PAGE_DIMENSIONS,
  IPage,
  IProduct,
} from '@/service/ts-definitions';
import { useSetupServices } from '@/service/useSetupServices';
import { getCustomDataControlSetting } from '@/settings/DataControlSettings';
import {
  pageSetupLocale,
  productSidebarLocale,
} from '@/settings/DefaultLocaleSettings';
import { settings } from '@/settings/DynamicSettings';
import { lowPrioritySizes } from '@/settings/SupplierSettings';
import { useCommit } from '@/store/rootStore';

const locale = {
  PROMPT_TEXT: 'Try now: Click on a product to see more information',
  VIRTUALS_ARE_ILLUSTRATIVE_TEXT:
    'Virtual product mockups are for illustrative purposes. The final decoration may differ.',
};

const commit = useCommit();
const { download } = useFileDownload();
const router = useRouter();
const route = useRoute();
const authService = useAuthService();
const stackableNotificationsHandlers = useStackableNotificationsHandlers();
const { isPhotoVirtualsSetting } = overrideDataTypeGuards();
const tryNowLocalStorage = useLocalStorage('tryNow');
const virtualsAreIllustrativeLocalStorage = useLocalStorage(
  'virtualsAreIllustrative'
);
const implicitTokenCookie = useCookie<ImplicitTokenResponse>('implicit-token');
const geolocationCookie = useCookie<GeolocationData>('geolocation');
useSetupServices();

const pageFromClickedProduct = ref<Page | undefined>();
const isTryNowNotificationOpen = ref(false);
const isVirtualsNotificationOpen = ref(false);
const pageIdToBeFetched = ref<string | undefined>();

const pageNumber = computed(() => Number(route.params.pageNumber));
const projectId = computed(() => route.params.projectId?.toString() ?? '');
const isDesignLoadedAndHasContent = computed(
  () =>
    designQuery.isSuccess.value &&
    Boolean(designQuery.data?.value?.pages?.length)
);
const priceLevel = computed(() =>
  companyResponse.data.value?.slug === settings.SLUGS.SHOWDOWNDISPLAYS
    ? 'highest'
    : 'lowest'
);

useQueryWrapper<AxiosResponse<ImplicitTokenResponse, any>>({
  options: {
    queryKey: 'implicit-token',
    queryFn: authService.requestImplicitToken,
    enabled: !implicitTokenCookie.get(),
  },
  handlers: {
    onSuccess(implicitTokenResponse) {
      if (!implicitTokenResponse) return;
      setImplicitTokenInCookie(implicitTokenResponse.data);
    },
  },
});
const geolocationQuery = useGeolocationQuery({
  options: {
    enabled: !geolocationCookie.get(),
  },
  handlers: {
    onSuccess(geolocationData) {
      if (!geolocationData) return;
      const timestamp = new Date().getTime();
      const expireFiveDays = timestamp + 60 * 60 * 24 * 1000 * 5;
      const expireDate = new Date(expireFiveDays);
      geolocationCookie.set(geolocationData, { expires: expireDate });
    },
  },
});
const trackingFactory = useMakeTrackingInstance({
  options: reactive({
    geolocation: computed(
      () => geolocationCookie.get() ?? geolocationQuery.data.value
    ),
  }),
});

const [designQuery, designLockedQuery, designStatus] = useGetDesign(
  reactive({ projectId }),
  {
    onSuccessDesignData(data) {
      document.title = data.name;
      commit({ type: 'project/setProject', payload: data });

      const newPageNumber = paginationFactory.instance.isOnPaginationRange
        ? pageNumber.value
        : 1;

      if (data.pages.length) assignPageDataToBeFetched(newPageNumber);
    },
  }
);
const companyResponse = useCompanyByIdQuery({
  options: reactive({
    companyId: toRef(() => designQuery.data.value?.company_id),
  }),
});

const companySettingSectionQuery = useCompanySettingSectionQuery({
  options: reactive({
    section: 'product-library',
    companyId: toRef(() => designQuery.data.value?.company_id),
  }),
});

const pageQuery = useGetPageByIdProject(
  reactive({
    projectId,
    pageId: pageIdToBeFetched,
  }),
  {
    onSuccessPage(data) {
      paginationFactory.handlers.setPages(data);
      paginationFactory.instance.loadedPagesIndexes.add(data.index);
      if (
        data.settings?.feature?.spreadPage === 'left' &&
        paginationFactory.handlers.isPageIndexOnPaginationRange(data.index) &&
        paginationFactory.handlers.isPageIndexInBatchRange(data.index)
      ) {
        assignPageDataToBeFetched(data.index + 1);
      }
    },
  }
);

const assignPageDataToBeFetched = (pageIndex?: number) => {
  if (pageIndex) {
    pageIdToBeFetched.value =
      paginationFactory.handlers.getPageIdByPageIndex(pageIndex);
    return;
  }
  paginationFactory.instance.basicPagesList?.forEach((basicPage) => {
    if (
      paginationFactory.handlers.isPageIndexInBatchRange(basicPage.index) &&
      !paginationFactory.instance.loadedPagesIndexes.has(basicPage.index)
    ) {
      pageIdToBeFetched.value = basicPage.page_id;
    }
  });
};

const paginationFactory = useMakePaginationInstance({
  options: reactive({
    initialPages: {},
    projectId,
    limit: 15,
    currency: toRef(() => designQuery.data.value?.currency ?? 'USD'),
    priceTableAttributes: toRef(() => {
      if (!isProductLibrarySetting(companySettingSectionQuery.data.value))
        return;

      const priceTableAttributes =
        companySettingSectionQuery.data.value['product-library']
          .price_table_attributes;

      if (!priceTableAttributes) return;

      return JSON.parse(priceTableAttributes);
    }),
    // This will be dynamic when we add :pageNumber as route param
    initialPageNumber: pageNumber.value || 1,
  }),
  handlers: {
    onPageChange(pageNumber) {
      router.push({
        name: 'Viewer',
        params: { pageNumber },
      });
    },
    onPageNotFound(pageNumber) {
      router.replace({
        name: 'Viewer',
        params: { pageNumber },
      });
    },
    onSetPages(page) {
      showTryNowNotification();
      showVirtualsIllustrativeNotification(page);
    },
  },
});

const {
  handlers: { openProductSidebar },
} = useMakeProductSidebarInstance({
  options: reactive({
    sidebarPricingModel: computed(() => {
      const companySettings = companySettingSectionQuery.data.value;

      if (isProductLibrarySetting(companySettings)) {
        return companySettings['product-library'].price_display ?? 'simple';
      }

      return 'simple';
    }),
  }),
  getters: {
    getProductData(slotId) {
      const productData = pageFromClickedProduct.value?.products[
        slotId
      ] as Product | null;
      const currencyKey = designQuery.data.value?.currency ?? 'USD';

      return formatNewProductPricing(
        productData,
        currencyKey,
        (() => {
          if (!isProductLibrarySetting(companySettingSectionQuery.data.value))
            return;

          const priceTableAttributes =
            companySettingSectionQuery.data.value['product-library']
              .price_table_attributes;

          if (!priceTableAttributes) return;

          return JSON.parse(priceTableAttributes);
        })(),
        priceLevel.value
      );
    },
    getProductSettings(slotId, page) {
      const productSettings = page.settings.products[slotId];
      if (!productSettings) {
        return mergeProductSettings({
          canvas: getCustomDataControlSetting(page.templateName),
          sidebar: {},
        });
      }

      return mergeProductSettings({
        canvas: isOldSettings(productSettings.canvas)
          ? transformOldSettings(productSettings.canvas)
          : productSettings.canvas,
        sidebar: isOldSettings(productSettings.sidebar)
          ? transformOldSettings(productSettings.sidebar)
          : productSettings.sidebar,
      });
    },
    getLocaleTranslations: () => productSidebarLocale,
  },
  onProductImageDownload: (slotId, page, url) => {
    download(url);
    trackingFactory.handlers.sendProductImageDownloadEvent({
      pages: [page],
      product: page.products[slotId] as any | undefined,
      imageUrl: url,
    });
  },
  onProductLinkClick: (slotId, page) => {
    trackingFactory.handlers.sendProductLinkClickEvent({
      component: 'view_on_website_button',
      pages: [page],
      product: page.products[slotId] as any | undefined,
      source: 'sidebar',
    });
  },
});

useMakePageSetupInstance({
  options: reactive({
    locale: pageSetupLocale,
    appName: '',
    mode: 'viewer',
    draggable: false,
    includeBrand: computed(
      () =>
        isProductLibrarySetting(companySettingSectionQuery.data.value) &&
        Boolean(
          Number(
            companySettingSectionQuery.data.value['product-library']
              .include_brand
          )
        )
    ) as unknown as boolean, // TODO: Fix type cast
    colorField: computed(() =>
      isProductLibrarySetting(companySettingSectionQuery.data.value)
        ? companySettingSectionQuery.data.value?.['product-library']
            ?.color_field ?? 'color'
        : 'color'
    ),
    productSlotProps: computed(() => ({
      currency: (designQuery.data.value?.currency as any | undefined) ?? 'USD',
      productIdKey: designQuery.data.value?.product_id_field || 'part_id',
      productPriceLabel:
        designQuery.data.value?.settings.pricingSettings?.price_label ||
        'starting_at',
    })) as any,
    productPricingSettings: computed(() => ({
      ...designQuery.data.value?.settings.pricingSettings,
      price_level: priceLevel.value,
    })) as any,
    designMarkUp: toRef(() => ({
      mode: designQuery.data.value?.settings.designMarkup?.mode ?? 'markup',
      percentage:
        designQuery.data.value?.settings.designMarkup?.percentage ?? '',
    })),
    isOnTemplateMode: false,
    type: 'design',
    useSkeletonLoader: true,
    lowPrioritySizes: toRef(() => {
      const lowPrioritySizesKeys = Object.keys(lowPrioritySizes);
      const companySlug = companyResponse.data.value?.slug;

      if (!companySlug || !lowPrioritySizesKeys.includes(companySlug))
        return [];
      const supplierKey = companySlug as keyof typeof lowPrioritySizes;
      return lowPrioritySizes[supplierKey];
    }),
    priceTableAttributes: toRef(() => {
      if (!isProductLibrarySetting(companySettingSectionQuery.data.value))
        return;

      const priceTableAttributes =
        companySettingSectionQuery.data.value['product-library']
          .price_table_attributes;

      if (!priceTableAttributes) return;

      return JSON.parse(priceTableAttributes);
    }),
    sidebarPricingModel: computed(() => {
      const companySettings = companySettingSectionQuery.data.value;

      if (isProductLibrarySetting(companySettings)) {
        return companySettings['product-library'].price_display ?? 'simple';
      }

      return 'simple';
    }),
  }),

  getters: {
    getProductData(slotId, page) {
      const productData = page.products[slotId] as Product | undefined;
      const currencyKey = designQuery.data.value?.currency ?? 'USD';

      return formatNewProductPricing(
        productData ?? null,
        currencyKey,
        (() => {
          if (!isProductLibrarySetting(companySettingSectionQuery.data.value))
            return;

          const priceTableAttributes =
            companySettingSectionQuery.data.value['product-library']
              .price_table_attributes;

          if (!priceTableAttributes) return;

          return JSON.parse(priceTableAttributes);
        })(),
        priceLevel.value
      ) as any;
    },
    getProductCardData() {
      return {} as any;
    },
    getProductMedia() {
      return {} as any;
    },
    getProductAssets() {
      return {} as any;
    },
    getProductSettings(slotId, page) {
      const productSettings = page.settings.products[slotId];
      if (!productSettings) {
        return mergeProductSettings({
          canvas: getCustomDataControlSetting(page.templateName),
          sidebar: {},
        });
      }

      return mergeProductSettings({
        canvas: isOldSettings(productSettings.canvas)
          ? transformOldSettings(productSettings.canvas)
          : productSettings.canvas,
        sidebar: isOldSettings(productSettings.sidebar)
          ? transformOldSettings(productSettings.sidebar)
          : productSettings.sidebar,
      });
    },
    getTemplateSettings(slotId, page) {
      const templateSettings = {
        manageSections:
          page.settings.template.manageSections ?? mergeTemplateSetting({}),

        manageProduct: {
          dataControls:
            (slotId
              ? page.settings.template.manageProduct?.[slotId]?.dataControls
              : null) ?? mergeTemplateSetting({}),

          canvasControls:
            (slotId
              ? page.settings.template.manageProduct?.[slotId]?.canvasControls
              : null) ?? mergeTemplateSetting({}),
        },
      };

      return templateSettings;
    },
    getProductImagesOptions(page) {
      if (page?.templateName === 'A4 Sell Sheet') {
        return {
          maxImageSelection: 8,
          maxPageImages: 2,
        };
      }
      return {
        maxImageSelection: 8,
        maxPageImages: 1,
      };
    },
    getLogoData(slotId, { page }) {
      const logoData = page.logos[slotId] as any | undefined;
      return logoData ? { url: logoData.url } : null;
    },
  },

  handlers: {
    onClickProduct(context) {
      pageFromClickedProduct.value = context.page;
      openProductSidebar(context);
      trackingFactory.handlers.sendProductClickEvent({
        product: context.page.products[context.slotId] as IProduct | undefined,
        pages: [context.page],
      });
    },
    onProductUrlClick({ page, slotId }) {
      trackingFactory.handlers.sendProductLinkClickEvent({
        component: 'product_card_button',
        pages: [page],
        product: page.products[slotId] as any | undefined,
        source: 'canvas',
      });
    },
  },
});

const pageOrientationFactory = useMakePageOrientationsInstance({
  initialDimensions:
    designQuery.data.value?.settings.designType?.dimensions ??
    DEFAULT_PAGE_DIMENSIONS,
  getters: {
    getDimensionsMap() {
      return {
        portrait: [816, 1056],
        landscape: [1225, 690],
      };
    },
  },
});

const isLoading = computed(
  () => designLockedQuery.isLoading.value || designQuery.isLoading.value
);

function showTryNowNotification() {
  if (isTryNowNotificationOpen.value || tryNowLocalStorage.get()) return;

  isTryNowNotificationOpen.value = true;
  stackableNotificationsHandlers.notify({
    text: locale.PROMPT_TEXT,
    imageUrl: '/images/smile.png',
    mediaBackgroundColor: 'white',
    onClose() {
      tryNowLocalStorage.set('true');
    },
  });
}

function showVirtualsIllustrativeNotification(_page: IPage) {
  if (
    isVirtualsNotificationOpen.value ||
    virtualsAreIllustrativeLocalStorage.get()
  )
    return;

  const productSettingsMap = _page?.settings?.products;
  const productSettingsList = Object.values(productSettingsMap ?? {});
  const pageHasVirtuals = productSettingsList.some((productSetting) => {
    const productImageSettings =
      productSetting?.canvas?.productImage?.override?.data;
    return isPhotoVirtualsSetting(productImageSettings);
  });

  if (!pageHasVirtuals && !designQuery.data.value?.settings.virtuals) return;

  isVirtualsNotificationOpen.value = true;
  stackableNotificationsHandlers.notify({
    text: locale.VIRTUALS_ARE_ILLUSTRATIVE_TEXT,
    imageUrl: '/images/sweatshirt.png',
    mediaBackgroundColor: 'white',
    onClose() {
      virtualsAreIllustrativeLocalStorage.set('true');
    },
  });
}

watch(
  [() => pageQuery.isSuccess.value, pageNumber],
  ([newPageQuerySuccessValue]) => {
    if (!newPageQuerySuccessValue) return;
    assignPageDataToBeFetched();
  },
  { flush: 'post' }
);

watch(
  () => designQuery.data.value?.settings?.designType?.dimensions,
  (nextDimensions) => {
    if (!nextDimensions) return;
    pageOrientationFactory.handlers.setPageOrientation(nextDimensions);
  }
);
</script>

<template>
  <DesignLocked v-if="designStatus === 'locked'" />
  <DesignPrivate v-else-if="designStatus === 'private'" />

  <template v-else>
    <PageLoader v-if="isLoading" />
    <ZDesign
      v-else-if="isDesignLoadedAndHasContent"
      :design="designQuery.data.value"
    />
    <DesignEmpty v-else />
  </template>
</template>
